<template>
  <div>
    <div v-if="error">
      <!-- Fallback UI when an error occurs -->
      <p>Something went wrong: {{ error.message }}</p>
      <pre v-if="isDev && error.stack">{{ error.stack }}</pre>
      <button @click="resetError">Try Again</button>
    </div>
    <div v-else>
      <!-- Render the child components if there is no error -->
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, onErrorCaptured, watch } from 'vue';
import { useRoute } from 'vue-router';

const error = ref(null);
const isDev = import.meta.env.DEV;

// Function to reset the error state and re-render the child components
const resetError = () => {
  error.value = null;
};

// Without this the fallback sticks for the rest of the session: one failing
// page leaves every later route showing "Something went wrong", which is why
// the error appears on screens that never actually threw. Navigating is the
// user asking for a fresh page, so clear the boundary.
const route = useRoute();
watch(() => route.fullPath, resetError);

// Catch errors from any descendant components
onErrorCaptured((err, instance, info) => {
  console.error("Error caught in ErrorBoundary:", err, info);
  error.value = err;
  // Deliberately not returning false: swallowing the error here would also
  // hide it from app.config.errorHandler, and therefore from Sentry, leaving
  // nothing to diagnose from. Show the fallback *and* let it be reported.
});

// Expose resetError to allow the parent to reset the boundary if needed
defineExpose({ resetError });
</script>
